import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, Smartphone, Mail, Copy, Check, AlertTriangle, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import mfaService from '../../services/mfaService';
import QRCode from 'qrcode';
import { getMfaResendCooldownSeconds } from '../../utils/mfaCooldown';

const MFASetup = ({ onSetupComplete, onCancel, userEmail, isRequired = false }) => {
  const [step, setStep] = useState(1);
  const [method, setMethod] = useState(null);
  const [deviceName, setDeviceName] = useState('');
  const [setupData, setSetupData] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);

  // Reset all state to initial values
  const resetToMethodSelection = () => {
    setStep(1);
    setMethod(null);
    setDeviceName('');
    setSetupData(null);
    setQrCodeUrl('');
    setVerificationCode('');
    setLoading(false);
    setCopied(false);
    setResendCooldown(0);
    setResending(false);
  };

  // Cooldown timer effect
  useEffect(() => {
    let interval;
    if (resendCooldown > 0) {
      interval = setInterval(() => {
        setResendCooldown(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendCooldown]);

  // Resend email verification code
  const resendEmailCode = async () => {
    if (resendCooldown > 0 || resending) return;
    
    try {
      setResending(true);
      const response = await mfaService.resendEmailCode();
      
      if (response.cooldown_remaining_seconds) {
        setResendCooldown(getMfaResendCooldownSeconds(response));
        toast.error(response.cooldown_message || 'Please wait before requesting another code.');
      } else {
        setResendCooldown(getMfaResendCooldownSeconds(response));
        toast.success('New verification code sent to your email!');
      }
    } catch (error) {
      if (error.response?.status === 429) {
        const data = error.response.data;
        setResendCooldown(getMfaResendCooldownSeconds(data));
        toast.error(data.cooldown_message || 'Please wait before requesting another code.');
      } else {
        toast.error('Failed to resend code. Please try again.');
      }
    } finally {
      setResending(false);
    }
  };

  const startSetup = async (selectedMethod) => {
    // For email verification, we don't need a device name
    if (selectedMethod === 'totp' && !deviceName.trim()) {
      toast.error('Please enter a device name for your authenticator app');
      return;
    }

    try {
      setLoading(true);
      
      // Use appropriate device name based on method
      const finalDeviceName = selectedMethod === 'email' 
        ? 'Email Verification' 
        : deviceName.trim();
      
      const data = await mfaService.setupMFA(selectedMethod, finalDeviceName);
      console.log('[MFA SETUP] Backend response:', data);
      console.log('[MFA SETUP] Selected method:', selectedMethod);
      
      // Set method and setup data together to ensure they're in sync
      setMethod(selectedMethod);
      setSetupData({ ...data, selectedMethod }); // Include selectedMethod in setupData for fallback

      if (selectedMethod === 'email') {
        setResendCooldown(getMfaResendCooldownSeconds(data));
      }
      
      if (selectedMethod === 'totp' && data.qr_code) {
        console.log('[MFA SETUP] QR code received from backend (base64):', data.qr_code.substring(0, 50) + '...');
        // Backend already provides base64 QR code, use it directly
        setQrCodeUrl(data.qr_code);
        console.log('[MFA SETUP] QR code set successfully');
      } else {
        console.log('[MFA SETUP] Email MFA setup - no QR code needed');
      }
      
      console.log('[MFA SETUP] Advancing to step 2 with method:', selectedMethod);
      setStep(2);
    } catch (error) {
      console.error('Error starting MFA setup:', error);
      toast.error('Failed to start MFA setup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifySetup = async () => {
    if (!verificationCode.trim()) {
      toast.error('Please enter the verification code');
      return;
    }

    try {
      setLoading(true);
      await mfaService.verifyMFASetup(verificationCode, method);
      toast.success('MFA setup completed successfully!');
      onSetupComplete();
    } catch (error) {
      console.error('Error verifying MFA setup:', error);
      toast.error('Invalid verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 6) {
      setVerificationCode(value);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center p-4 z-50"
      onClick={(e) => {
        // Only allow closing by clicking backdrop if MFA is not required
        if (e.target === e.currentTarget && onCancel && !isRequired) {
          onCancel();
        }
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <Shield className="w-6 h-6 text-primary-600 mr-2" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Set Up Multi-Factor Authentication
              </h2>
            </div>
            {onCancel && !isRequired && (
              <button
                onClick={onCancel}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {step === 1 && !method && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-4"
            >
              <div className={`rounded-lg p-4 mb-6 ${
                isRequired 
                  ? 'bg-red-50 border border-red-200' 
                  : 'bg-yellow-50 border border-yellow-200'
              }`}>
                <div className="flex items-start">
                  <AlertTriangle className={`w-5 h-5 mr-2 mt-0.5 ${
                    isRequired ? 'text-red-600' : 'text-yellow-600'
                  }`} />
                  <div>
                    <p className={`text-sm font-medium ${
                      isRequired ? 'text-red-800' : 'text-yellow-800'
                    }`}>
                      {isRequired ? 'MFA Setup Required - Cannot Skip' : 'MFA Required'}
                    </p>
                    <p className={`text-sm mt-1 ${
                      isRequired ? 'text-red-700' : 'text-yellow-700'
                    }`}>
                      {isRequired 
                        ? 'You must complete multi-factor authentication setup to continue. This step cannot be skipped.'
                        : 'Your account requires multi-factor authentication for enhanced security. Please choose a method to set up MFA.'
                      }
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => setMethod('totp')}
                  disabled={loading}
                  className="w-full p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors duration-200 text-left disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center">
                    <Smartphone className="w-6 h-6 text-primary-600 mr-3" />
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-gray-100">Authenticator App</h3>
                      <p className="text-sm text-gray-500">
                        Use Google Authenticator, Authy, or similar apps
                      </p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400 ml-auto" />
                  </div>
                </button>

                <button
                  onClick={() => startSetup('email')}
                  disabled={loading}
                  className="w-full p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors duration-200 text-left disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center">
                    <Mail className="w-6 h-6 text-primary-600 mr-3" />
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-gray-100">Email Verification</h3>
                      <p className="text-sm text-gray-500">
                        Receive codes via email at {userEmail}
                      </p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400 ml-auto" />
                  </div>
                </button>
              </div>

              {loading && (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                </div>
              )}
            </motion.div>
          )}

          {/* Step 1.5: Device Name for TOTP only */}
          {step === 1 && method === 'totp' && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Name Your Device
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Give your authenticator device a memorable name
                </p>
              </div>

              <div className="mb-6">
                <label htmlFor="deviceName" className="block text-sm font-medium text-gray-700 mb-2">
                  Device Name
                </label>
                <input
                  id="deviceName"
                  type="text"
                  value={deviceName}
                  onChange={(e) => setDeviceName(e.target.value)}
                  placeholder="e.g., My iPhone, Work Laptop, Personal Computer"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
                  maxLength={50}
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">
                  This helps you identify this device later when managing your MFA methods.
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={resetToMethodSelection}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={() => startSetup('totp')}
                  disabled={loading || !deviceName.trim()}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Setting up...
                    </div>
                  ) : (
                    'Continue'
                  )}
                </button>
              </div>
            </motion.div>
          )}

          {step === 2 && method === 'totp' && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Scan QR Code
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Scan this QR code with your authenticator app
                </p>
                
                {qrCodeUrl && (
                  <div className="flex justify-center mb-4">
                    <img src={qrCodeUrl} alt="QR Code" className="w-48 h-48" />
                  </div>
                )}

                {setupData?.manual_entry_key && (
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-4">
                    <p className="text-xs text-gray-600 mb-2">
                      Can't scan? Enter this code manually:
                    </p>
                    <div className="flex items-center justify-center space-x-2">
                      <code className="text-sm font-mono bg-white dark:bg-gray-700 px-2 py-1 rounded border dark:border-gray-600">
                        {setupData.manual_entry_key}
                      </code>
                      <button
                        onClick={() => copyToClipboard(setupData.manual_entry_key)}
                        className="text-primary-600 hover:text-primary-700"
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter verification code from your app:
                </label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={handleCodeChange}
                  placeholder="000000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-center text-lg font-mono"
                  maxLength={6}
                />
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={resetToMethodSelection}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  Back
                </button>
                <button
                  onClick={verifySetup}
                  disabled={loading || verificationCode.length !== 6}
                  className="flex-1 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Verifying...' : 'Complete Setup'}
                </button>
              </div>
            </motion.div>
          )}

          {step === 2 && (method === 'email' || setupData?.selectedMethod === 'email') && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Email Verification Setup
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  A verification code has been sent to {userEmail}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter the verification code from your email:
                </label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={handleCodeChange}
                  placeholder="000000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-center text-lg font-mono"
                  maxLength={6}
                />
              </div>

              {/* Resend Code Section */}
              <div className="text-center">
                <button
                  onClick={resendEmailCode}
                  disabled={resending || resendCooldown > 0}
                  className="text-sm text-primary-600 hover:text-primary-700 disabled:text-gray-400 disabled:cursor-not-allowed underline"
                >
                  {resending ? (
                    <span className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary-600 mr-2"></div>
                      Sending...
                    </span>
                  ) : resendCooldown > 0 ? (
                    `Resend code in ${Math.floor(resendCooldown / 60)}:${(resendCooldown % 60).toString().padStart(2, '0')}`
                  ) : (
                    'Resend verification code'
                  )}
                </button>
                {resendCooldown === 0 && !resending && (
                  <p className="text-xs text-gray-500 mt-1">
                    Didn't receive the code? Click above to resend.
                  </p>
                )}
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={resetToMethodSelection}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  Back
                </button>
                <button
                  onClick={verifySetup}
                  disabled={loading || verificationCode.length !== 6}
                  className="flex-1 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Verifying...' : 'Complete Setup'}
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default MFASetup;
